import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, Clock, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * PriorityCard — driver-side. When this driver receives a `priority_dispatch`
 * Socket.IO event they get a high-emphasis card with a 15s countdown and a
 * "Quick Accept @ passenger fare" button. Disappears on expiry / accept.
 */
export default function PriorityCard({ payload, onTakeRide, onDismiss }) {
  const [secs, setSecs] = useState(15);
  const ride = payload?.ride;
  const expiresAt = payload?.expires_at;

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const left = Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setSecs(left);
      if (left <= 0) onDismiss?.();
    };
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [expiresAt, onDismiss]);

  if (!ride) return null;
  const fare = ride.passenger_offer ?? ride.estimated_fare;
  const accept = async () => {
    try {
      await api.post(`/chauffeur/rides/${ride.id}/offers`, {
        fare: Number(fare), eta_minutes: 6,
      });
      toast.success("Priority offer sent");
      onTakeRide?.(ride.id, fare);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not respond");
    }
  };
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8 }}
        data-testid={`priority-card-${ride.id}`}
        className="relative p-4 border border-gold bg-gold/15 rounded-md shadow-[0_0_24px_rgba(212,175,55,0.25)]">
        <div className="absolute -top-2 left-3 px-2 py-0.5 bg-gold text-luxury-black text-[10px] font-body uppercase tracking-[0.18em] rounded-sm flex items-center gap-1">
          <Crown size={10} /> Priority {payload.rank ? `#${payload.rank}` : ""}
        </div>
        <button onClick={onDismiss} data-testid={`priority-dismiss-${ride.id}`}
          className="absolute top-2 right-2 text-white/40 hover:text-white"><X size={14} /></button>
        <div className="flex items-center justify-between mb-1.5 mt-1">
          <span className="text-xs font-body uppercase tracking-wider text-gold">
            {ride.type === "food" ? "Food Delivery" : "Taxi"}
          </span>
          <span className="text-xs font-body text-gold/80 inline-flex items-center gap-1">
            <Clock size={11} /> {secs}s
          </span>
        </div>
        <p className="text-white text-sm font-body">{ride.pickup?.address}</p>
        <p className="text-white/60 text-xs font-body">→ {ride.dropoff?.address}</p>
        <p className="text-white/40 text-[10px] font-body mt-1">
          {ride.distance_km} km · passenger offer <span className="text-gold">JMD {fare}</span>
        </p>
        <button data-testid={`priority-accept-${ride.id}`} onClick={accept}
          className="mt-3 w-full bg-gold text-luxury-black py-2 text-xs font-body uppercase tracking-wider hover:bg-gold/90 transition-colors">
          Quick accept @ JMD {fare}
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
